import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Eye, Trash2, Search, ShoppingCart, Clock, AlertCircle } from 'lucide-react';
import { ordersApi } from '../api/cms';
import type { Order, OrderStatus } from '../types';
import { useLang } from '../context/LangContext';

const STATUS_CONFIG: Record<OrderStatus, { label: string; labelEn: string; color: string }> = {
  pending:     { label: 'În așteptare', labelEn: 'Pending',     color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  confirmed:   { label: 'Confirmat',    labelEn: 'Confirmed',   color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  in_progress: { label: 'În execuție',  labelEn: 'In Progress', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  completed:   { label: 'Finalizat',    labelEn: 'Completed',   color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  cancelled:   { label: 'Anulat',       labelEn: 'Cancelled',   color: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

/* ── AbandonedCartsTab ───────────────────────────────────────────────────── */
interface AbandonedCart {
  sessionId: string;
  ip: string;
  fingerprint?: string;
  email?: string;
  items: { name: string; price: number; currency: string; qty: number }[];
  total: number;
  currency: string;
  createdAt: string;
  lastSeen: string;
}

function AbandonedCartsTab() {
  const [carts, setCarts] = useState<AbandonedCart[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<AbandonedCart | null>(null);

  useEffect(() => {
    fetch('/cms-api/carts.php', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setCarts(d.data || d || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const delCart = async (sessionId: string) => {
    if (!confirm('Ștergi coșul abandonat?')) return;
    await fetch('/cms-api/carts.php', {
      method: 'DELETE', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    });
    setCarts(c => c.filter(x => x.sessionId !== sessionId));
    if (selected?.sessionId === sessionId) setSelected(null);
  };

  const totalValue = carts.reduce((s, c) => s + (c.currency === 'EUR' ? c.total * 5 : c.total), 0);

  if (loading) return <div className="py-16 text-center text-white/30 text-sm">Se încarcă...</div>;

  return (
    <div className="space-y-5">
      {/* KPI */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Coșuri abandonate', value: carts.length, icon: ShoppingCart, color: 'text-yellow-400' },
          { label: 'Valoare pierdută est.', value: `${(totalValue/5).toFixed(0)} EUR`, icon: AlertCircle, color: 'text-red-400' },
          { label: 'Ultima activitate', value: carts[0] ? new Date(carts[0].lastSeen).toLocaleDateString('ro-RO') : '—', icon: Clock, color: 'text-[#4ab8ff]' },
        ].map(s => (
          <div key={s.label} className="bg-[#07090f] border border-white/10 rounded-xl px-4 py-3">
            <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-white/40 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {carts.length === 0 ? (
        <div className="py-16 text-center">
          <ShoppingCart size={40} className="mx-auto text-white/10 mb-3" />
          <p className="text-white/30 text-sm">Niciun coș abandonat înregistrat.</p>
          <p className="text-white/20 text-xs mt-1">Coșurile apar automat când vizitatorii adaugă produse fără a finaliza comanda.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Lista */}
          <div className="bg-[#07090f] border border-white/10 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-white/5 text-xs text-white/40 font-medium uppercase tracking-wide">
              {carts.length} coșuri abandonate
            </div>
            <div className="divide-y divide-white/5 max-h-[500px] overflow-y-auto">
              {carts.map(c => (
                <div key={c.sessionId}
                  className={`px-4 py-3.5 cursor-pointer transition-colors ${selected?.sessionId === c.sessionId ? 'bg-[#295CA8]/15' : 'hover:bg-white/[0.02]'}`}
                  onClick={() => setSelected(c)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium truncate">
                        {c.email || c.fingerprint?.slice(0, 12) || c.ip}
                      </div>
                      <div className="text-xs text-white/35 mt-0.5">
                        {c.items.length} produse · {c.total} {c.currency}
                      </div>
                      <div className="text-xs text-white/20 mt-0.5">{c.ip}</div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <div className="text-right">
                        <div className="text-xs text-white/50">{new Date(c.lastSeen).toLocaleDateString('ro-RO')}</div>
                        <div className="text-xs text-white/25">{new Date(c.lastSeen).toLocaleTimeString('ro-RO', {hour:'2-digit',minute:'2-digit'})}</div>
                      </div>
                      <button onClick={e => { e.stopPropagation(); delCart(c.sessionId); }}
                        className="p-1.5 rounded text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-all">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Detalii */}
          {selected ? (
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-white">Detalii coș abandonat</h3>
                <button onClick={() => setSelected(null)} className="text-xs text-white/30 hover:text-white">× Închide</button>
              </div>
              <div className="space-y-1.5 text-xs">
                <div className="flex gap-2"><span className="text-white/40 w-24">IP:</span><span className="text-white">{selected.ip}</span></div>
                {selected.fingerprint && <div className="flex gap-2"><span className="text-white/40 w-24">Fingerprint:</span><span className="text-white/70 font-mono">{selected.fingerprint.slice(0,20)}…</span></div>}
                {selected.email && <div className="flex gap-2"><span className="text-white/40 w-24">Email:</span><span className="text-[#4ab8ff]">{selected.email}</span></div>}
                <div className="flex gap-2"><span className="text-white/40 w-24">Prima vedere:</span><span className="text-white">{new Date(selected.createdAt).toLocaleString('ro-RO')}</span></div>
                <div className="flex gap-2"><span className="text-white/40 w-24">Ultima act.:</span><span className="text-white">{new Date(selected.lastSeen).toLocaleString('ro-RO')}</span></div>
              </div>
              <div>
                <div className="text-xs font-semibold text-white/40 uppercase tracking-wide mb-2">Produse în coș</div>
                <div className="space-y-2">
                  {selected.items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-2 border-b border-white/5">
                      <span className="text-white truncate flex-1">{item.qty}× {item.name}</span>
                      <span className="text-[#4ab8ff] ml-3 font-semibold whitespace-nowrap">{(item.price * item.qty).toFixed(2)} {item.currency}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-3 text-sm font-bold">
                  <span className="text-white/50">Total:</span>
                  <span className="text-[#4ab8ff]">{selected.total} {selected.currency}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-[#07090f] border border-white/5 rounded-xl p-8 flex items-center justify-center text-center text-white/20 text-sm">
              Selectează un coș pentru a vedea detaliile
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function OrdersPage() {
  const { lang, t } = useLang();
  const [activeTab, setActiveTab] = useState<'orders' | 'abandoned'>('orders');
  const [orders, setOrders] = useState<Order[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | ''>('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    ordersApi.list().then(setOrders).catch((_e: unknown) => setOrders([])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const del = async (id: string, orderNumber: string) => {
    if (!confirm(`${t('Ștergeți comanda', 'Delete order')} ${orderNumber}?`)) return;
    await ordersApi.delete(id).catch((_e: unknown): null => null);
    setOrders(prev => prev.filter(o => o.id !== id));
  };

  const filtered = orders.filter(o =>
    (o.orderNumber.toLowerCase().includes(filter.toLowerCase()) ||
     o.clientName.toLowerCase().includes(filter.toLowerCase()) ||
     o.clientCompany.toLowerCase().includes(filter.toLowerCase()) || !filter) &&
    (o.status === statusFilter || !statusFilter)
  );

  const totals = {
    all: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    in_progress: orders.filter(o => o.status === 'in_progress').length,
    confirmed: orders.filter(o => o.status === 'confirmed').length,
  };

  const revenue = orders.filter(o => o.status === 'completed').reduce((s, o) => s + o.total, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('Comenzi', 'Orders')}</h1>
          <p className="text-white/40 text-sm mt-1">{t('Gestionează comenzile clienților', 'Manage client orders')}</p>
        </div>
        {activeTab === 'orders' && (
          <Link to="/orders/new"
            className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            <Plus size={16} /> {t('Comandă nouă', 'New Order')}
          </Link>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10">
        {[
          { key: 'orders' as const, label: 'Comenzi', icon: Eye },
          { key: 'abandoned' as const, label: 'Coșuri abandonate', icon: ShoppingCart },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === key
                ? 'border-[#4ab8ff] text-[#4ab8ff]'
                : 'border-transparent text-white/40 hover:text-white hover:border-white/20'
            }`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {activeTab === 'abandoned' && <AbandonedCartsTab />}
      {activeTab === 'orders' && (<>

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: t('Total comenzi', 'Total orders'), value: totals.all, color: 'text-white' },
          { label: t('În așteptare', 'Pending'), value: totals.pending, color: 'text-yellow-400' },
          { label: t('Confirmate', 'Confirmed'), value: totals.confirmed, color: 'text-blue-400' },
          { label: t('Venituri finalizate', 'Completed revenue'), value: `${revenue.toLocaleString()} EUR`, color: 'text-green-400' },
        ].map(s => (
          <div key={s.label} className="bg-[#07090f] border border-white/10 rounded-xl px-4 py-3">
            <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-white/40 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input value={filter} onChange={e => setFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors"
            placeholder={t('Caută număr, client...', 'Search order, client...')} />
        </div>
        <div className="relative flex items-center gap-2">
          <Search size={14} className="text-white/30 absolute left-3" />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as OrderStatus | '')}
            className="bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-colors">
            <option value="">{t('Toate statusurile', 'All statuses')}</option>
            {(Object.keys(STATUS_CONFIG) as OrderStatus[]).map(s => (
              <option key={s} value={s}>{lang === 'ro' ? STATUS_CONFIG[s].label : STATUS_CONFIG[s].labelEn}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#07090f] border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-white/30 text-sm">{t('Se încarcă...', 'Loading...')}</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-white/30 text-sm">{t('Nicio comandă găsită.', 'No orders found.')}</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-5 py-3 text-xs text-white/40 font-medium uppercase tracking-wide">{t('Nr. Comandă', 'Order #')}</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wide hidden md:table-cell">{t('Client', 'Client')}</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wide hidden lg:table-cell">{t('Produse', 'Items')}</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wide hidden sm:table-cell">{t('Total', 'Total')}</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wide">{t('Status', 'Status')}</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wide hidden md:table-cell">{t('Data', 'Date')}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map(o => {
                const cfg = STATUS_CONFIG[o.status];
                return (
                  <tr key={o.id} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-5 py-3.5">
                      <span className="text-sm font-mono text-[#4ab8ff] font-medium">{o.orderNumber}</span>
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell">
                      <div className="text-sm text-white font-medium">{o.clientName}</div>
                      <div className="text-xs text-white/30">{o.clientCompany}</div>
                    </td>
                    <td className="px-4 py-3.5 hidden lg:table-cell">
                      <div className="text-xs text-white/50">{o.items.map(i => i.productName).join(', ')}</div>
                    </td>
                    <td className="px-4 py-3.5 hidden sm:table-cell">
                      <span className="text-sm text-white font-semibold">{o.total.toLocaleString()} {o.currency}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
                        {lang === 'ro' ? cfg.label : cfg.labelEn}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-xs text-white/40 hidden md:table-cell">{o.createdAt}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2 justify-end">
                        <Link to={`/orders/${o.id}`}
                          className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-all">
                          <Eye size={14} />
                        </Link>
                        <button onClick={() => del(o.id, o.orderNumber)}
                          className="p-1.5 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-all">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>)}
    </div>
  );
}
